/**
 * Administración de categorías en el libro: cambiar su tipo, naturaleza y palabras clave; quitar una regla;
 * renombrar o fusionar con otra; eliminar una que ya no se usa.
 *
 * El nombre de una categoría está escrito en muchas pestañas (Movimientos E, Divisiones B, Presupuesto B, Notas B,
 * _Reglas G, Previstos B, Archivo E, Histórico B). Renombrar o fusionar las cambia TODAS en una sola escritura
 * (values.batchUpdate se aplica entera o no se aplica), así que un reintento no duplica nada:
 * - renombrar: cada celda con el nombre viejo pasa a tener el nuevo;
 * - fusionar (el nombre nuevo ya existe): además, lo que coincide se suma en la fila de la que queda
 *   (presupuesto del mismo año, histórico del mismo año) o se junta (nota del mismo mes), y la fila de la que
 *   desaparece se vacía. Sus palabras clave pasan a la que queda.
 */
import type { BudgetRow, Category, Rule } from '@pf/domain';
import { num, text } from './cells.js';
import type { NoteRow, Values } from './parse.js';

type Cell = string | number | boolean;
type Update = { range: string; values: Cell[][] };
interface Api {
  get(range: string): Promise<Values>;
  update(data: Array<{ range: string; values: Values }>, input?: 'RAW' | 'USER_ENTERED'): Promise<void>;
  deleteRows(rows: Array<{ tab: string; row: number }>): Promise<void>;
}
type Result = { status: 'done' } | { status: 'conflict'; message: string };

const same = (a: unknown, b: string) => text(a).toLowerCase() === b.trim().toLowerCase();
const KIND_LABEL = { expense: 'Gasto', income: 'Ingreso', excluded: 'No cuenta' } as const;
const NATURE_LABEL = { fixed: 'Fijo', variable: 'Variable', '': '' } as const;
/** Pestañas opcionales: si no existen, no hay nada que cambiar en ellas. */
const read = async (api: Api, range: string): Promise<Values> => { try { return await api.get(range); } catch { return []; } };

/** Presupuesto de dos categorías fusionadas: se suma mes a mes (y se avisa si alguna avisaba). */
export function mergeBudget(keep: BudgetRow, gone: BudgetRow): BudgetRow {
  const at = (b: BudgetRow, i: number) => b.overrides[i] ?? b.monthly;
  return { ...keep, monthly: Math.round((keep.monthly + gone.monthly) * 100) / 100,
    overrides: keep.overrides.map((o, i) => (o === null && (gone.overrides[i] ?? null) === null ? null : Math.round((at(keep, i) + at(gone, i)) * 100) / 100)),
    warn: keep.warn || gone.warn, note: [keep.note, gone.note].filter(Boolean).join(' · ') };
}
export const mergeNote = (keep: string, gone: string) => [keep, gone].filter(Boolean).join(' · ');
export const mergeKeywords = (keep: string[], gone: string[]) => [...new Set([...keep, ...gone].map(k => k.trim()).filter(Boolean))];

export const NEXT_MONTH_HEADER = 'Mes siguiente desde el día';

export async function updateCategory(api: Api, name: string, c: Category): Promise<Result> {
  const all = await api.get("'Categorías'!A1:E");
  const i = all.findIndex((r, n) => n > 0 && same(r?.[0], name));
  if (i < 0) return { status: 'conflict', message: `La categoría ${name} ya no está en el libro` };
  const data: Array<{ range: string; values: Values }> = [{ range: `'Categorías'!B${i + 1}:D${i + 1}`, values: [[KIND_LABEL[c.kind], c.kind === 'expense' ? NATURE_LABEL[c.nature] : '', c.keywords.join(', ')]] }];
  // «Cuenta en el mes siguiente desde el día…» (columna E): su título se pone la primera vez
  if (c.nextMonthFrom !== undefined) {
    if (!text(all[0]?.[4])) data.push({ range: "'Categorías'!E1", values: [[NEXT_MONTH_HEADER]] });
    data.push({ range: `'Categorías'!E${i + 1}`, values: [[c.nextMonthFrom ?? '']] });
  }
  await api.update(data);
  return { status: 'done' };
}

/** Las reglas no tienen Id: se reconoce por su contenido (cuenta, texto, expresión, importe, dirección y categoría). */
const ruleCells = (r: Rule) => [r.account, r.contains, r.regex, r.amountEquals ?? '', r.direction, r.category].map(v => text(v).toLowerCase());
const DIR: Record<string, string> = { gasto: 'expense', ingreso: 'income' };
const rowCells = (x: unknown[]) => [x[1], x[2], x[3], x[4], DIR[text(x[5]).toLowerCase()] ?? x[5], x[6]].map(v => text(v).toLowerCase());

export async function deleteRule(api: Api, rule: Rule): Promise<Result> {
  const all = await api.get('_Reglas!A1:H');
  const want = JSON.stringify(ruleCells(rule));
  const i = all.findIndex((r, n) => n > 0 && JSON.stringify(rowCells(r ?? [])) === want);
  if (i > 0) await api.update([{ range: `_Reglas!A${i + 1}:H${i + 1}`, values: [Array(8).fill('')] }]);
  return { status: 'done' };   // si ya no está, ya está quitada
}

/** Renombrar «from» a «to», o fusionarla con «to» si esa ya existe. */
export async function renameCategory(api: Api, from: string, to: string): Promise<Result> {
  to = to.trim();
  if (!to || same(from, to) && from.trim() === to) return { status: 'done' };
  const cats = await api.get("'Categorías'!A1:D");
  const iFrom = cats.findIndex((r, n) => n > 0 && same(r?.[0], from));
  const iTo = cats.findIndex((r, n) => n > 0 && n !== iFrom && same(r?.[0], to));
  if (iFrom < 0) return iTo > 0 ? { status: 'done' } : { status: 'conflict', message: `La categoría ${from} ya no está en el libro` };
  const merge = iTo > 0;
  if (merge) to = text(cats[iTo]![0]);   // el nombre tal como está escrito en la que queda
  const updates: Update[] = [];

  // 1) Celdas con el nombre en las pestañas que solo lo nombran
  for (const [tab, col] of [['Movimientos', 'E'], ['Divisiones', 'B'], ['_Reglas', 'G'], ['Previstos', 'B'], ['Archivo', 'E']] as const) {
    const rows = await read(api, `${tab}!${col}1:${col}`);
    rows.forEach((r, n) => { if (n > 0 && same(r?.[0], from)) updates.push({ range: `${tab}!${col}${n + 1}`, values: [[to]] }); });
  }

  // 2) Presupuesto (por año): renombrar, o sumar con la del mismo año y vaciar la fila
  const budget = await read(api, 'Presupuesto!A1:Q');
  const bRow = (r: unknown[]): BudgetRow => ({ year: num(r[0]) ?? 0, category: text(r[1]), monthly: num(r[2]) ?? 0,
    overrides: Array.from({ length: 12 }, (_, i) => num(r[3 + i])), warn: r[15] === true || /^(true|sí|si|1)$/i.test(text(r[15])), note: text(r[16]) });
  budget.forEach((r, n) => {
    if (n === 0 || !same(r?.[1], from)) return;
    const k = budget.findIndex((x, m) => m > 0 && m !== n && same(x?.[1], to) && num(x?.[0]) === num(r?.[0]));
    if (k < 0) { updates.push({ range: `Presupuesto!B${n + 1}`, values: [[to]] }); return; }
    const b = mergeBudget(bRow(budget[k]!), bRow(r!));
    updates.push({ range: `Presupuesto!A${k + 1}:Q${k + 1}`, values: [[b.year, text(budget[k]![1]), b.monthly, ...b.overrides.map(o => o ?? ''), b.warn, b.note]] });
    updates.push({ range: `Presupuesto!A${n + 1}:Q${n + 1}`, values: [Array(17).fill('')] });
  });

  // 3) Notas (por mes): renombrar, o juntar con la nota del mismo mes y vaciar la fila
  const notes = await read(api, 'Notas!A1:C');
  notes.forEach((r, n) => {
    if (n === 0 || !same(r?.[1], from)) return;
    const k = notes.findIndex((x, m) => m > 0 && m !== n && same(x?.[1], to) && text(x?.[0]) === text(r?.[0]));
    if (k < 0) { updates.push({ range: `Notas!B${n + 1}`, values: [[to]] }); return; }
    updates.push({ range: `Notas!C${k + 1}`, values: [[mergeNote(text(notes[k]![2]), text(r?.[2]))]] });
    updates.push({ range: `Notas!A${n + 1}:C${n + 1}`, values: [['', '', '']] });
  });

  // 4) Histórico (años archivados, por año): renombrar, o sumar los meses con la del mismo año y vaciar la fila
  const hist = await read(api, "'Histórico'!A1:P");
  hist.forEach((r, n) => {
    if (n === 0 || !same(r?.[1], from)) return;
    const k = hist.findIndex((x, m) => m > 0 && m !== n && same(x?.[1], to) && num(x?.[0]) === num(r?.[0]));
    if (k < 0) { updates.push({ range: `'Histórico'!B${n + 1}`, values: [[to]] }); return; }
    const sum = Array.from({ length: 13 }, (_, i) => Math.round(((num(hist[k]![3 + i]) ?? 0) + (num(r![3 + i]) ?? 0)) * 100) / 100);
    updates.push({ range: `'Histórico'!D${k + 1}:P${k + 1}`, values: [sum] });
    updates.push({ range: `'Histórico'!A${n + 1}:P${n + 1}`, values: [Array(16).fill('')] });
  });

  // 5) Categorías: la fila cambia de nombre, o se vacía y sus palabras pasan a la que queda
  if (!merge) updates.push({ range: `'Categorías'!A${iFrom + 1}`, values: [[to]] });
  else {
    const kw = (r: unknown[] | undefined) => text(r?.[3]).split(',').map(s => s.trim()).filter(Boolean);
    updates.push({ range: `'Categorías'!D${iTo + 1}`, values: [[mergeKeywords(kw(cats[iTo]), kw(cats[iFrom])).join(', ')]] });
    updates.push({ range: `'Categorías'!A${iFrom + 1}:D${iFrom + 1}`, values: [['', '', '', '']] });
  }
  await api.update(updates);
  // Limpieza: la fila vacía de Categorías se quita (si falla, no pasa nada: una fila vacía se ignora)
  if (merge) await api.deleteRows([{ tab: 'Categorías', row: iFrom + 1 }]).catch(() => undefined);
  return { status: 'done' };
}

/**
 * Eliminar una categoría que ya no usa ningún movimiento: se quitan con ella su presupuesto, sus notas y las reglas
 * que la ponían. (La pantalla solo lo ofrece si no la usa nada más; aquí no se comprueba el archivo.)
 */
export async function deleteCategory(api: Api, name: string): Promise<Result> {
  const updates: Update[] = [];
  const budget = await read(api, 'Presupuesto!A1:Q');
  budget.forEach((r, n) => { if (n > 0 && same(r?.[1], name)) updates.push({ range: `Presupuesto!A${n + 1}:Q${n + 1}`, values: [Array(17).fill('')] }); });
  const notes = await read(api, 'Notas!A1:C');
  notes.forEach((r, n) => { if (n > 0 && same(r?.[1], name)) updates.push({ range: `Notas!A${n + 1}:C${n + 1}`, values: [['', '', '']] }); });
  const rules = await read(api, '_Reglas!A1:H');
  rules.forEach((r, n) => { if (n > 0 && same(r?.[6], name)) updates.push({ range: `_Reglas!A${n + 1}:H${n + 1}`, values: [Array(8).fill('')] }); });
  const cats = await api.get("'Categorías'!A1:D");
  const i = cats.findIndex((r, n) => n > 0 && same(r?.[0], name));
  if (i > 0) updates.push({ range: `'Categorías'!A${i + 1}:D${i + 1}`, values: [['', '', '', '']] });
  if (updates.length) await api.update(updates);
  if (i > 0) await api.deleteRows([{ tab: 'Categorías', row: i + 1 }]).catch(() => undefined);
  return { status: 'done' };
}

/** Lo mismo aplicado a las notas en memoria (para la vista inmediata del dispositivo). */
export function renameNotes(notes: NoteRow[], from: string, to: string): NoteRow[] {
  const out: NoteRow[] = [];
  for (const n of notes) {
    if (same(n.category, from)) {
      // Sin nota de la que queda ese mes: solo cambia de nombre; con nota, se junta abajo
      if (!notes.some(x => !same(x.category, from) && same(x.category, to) && x.month === n.month)) out.push({ ...n, category: to });
    } else if (same(n.category, to)) {
      const gone = notes.find(x => same(x.category, from) && x.month === n.month);
      out.push(gone ? { ...n, note: mergeNote(n.note, gone.note) } : n);
    } else out.push(n);
  }
  return out;
}
